/**
 * Security Utilities for Frontend
 * Protects against XSS, injection attacks, and malicious input
 */

/**
 * Sanitize string input to prevent XSS attacks
 * Removes HTML tags and dangerous characters
 */
export const sanitizeInput = (input) => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Remove HTML tags
  let sanitized = input.replace(/<[^>]*>/g, '');
  
  // Remove dangerous JavaScript patterns
  sanitized = sanitized
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '') // Remove event handlers like onclick=
    .replace(/<script/gi, '')
    .replace(/<\/script>/gi, '')
    .replace(/<iframe/gi, '')
    .replace(/<object/gi, '')
    .replace(/<embed/gi, '')
    .replace(/<link/gi, '')
    .replace(/<style/gi, '');
  
  // Trim whitespace
  sanitized = sanitized.trim();
  
  return sanitized;
};

/**
 * Sanitize text for display (allows safe HTML but escapes dangerous content)
 * Use this when you need to display user input in HTML
 */
export const escapeHtml = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
    '/': '&#x2F;',
  };

  return text.replace(/[&<>"'/]/g, (char) => map[char]);
};

/**
 * Validate and sanitize title input
 * - Max 100 characters
 * - Min 10 characters
 * - No HTML tags
 * - No dangerous patterns
 */
export const validateTitle = (title) => {
  if (!title || typeof title !== 'string') {
    return { valid: false, error: 'Title is required' };
  }

  const sanitized = sanitizeInput(title);
  
  if (sanitized.length < 10) {
    return { valid: false, error: 'Title must be at least 10 characters' };
  }
  
  if (sanitized.length > 100) {
    return { valid: false, error: 'Title must be less than 100 characters' };
  }

  // Check for dangerous patterns
  const dangerousPatterns = [
    /<script/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /<iframe/gi,
    /<object/gi,
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(sanitized)) {
      return { valid: false, error: 'Title contains invalid characters' };
    }
  }

  return { valid: true, sanitized };
};

/**
 * Validate and sanitize description input
 * - Max 500 characters
 * - Min 20 characters
 * - No HTML tags
 * - No dangerous patterns
 */
export const validateDescription = (description) => {
  if (!description || typeof description !== 'string') {
    return { valid: false, error: 'Description is required' };
  }

  const sanitized = sanitizeInput(description);
  
  if (sanitized.length < 20) {
    return { valid: false, error: 'Description must be at least 20 characters' };
  }
  
  if (sanitized.length > 500) {
    return { valid: false, error: 'Description must be less than 500 characters' };
  }

  // Check for dangerous patterns
  const dangerousPatterns = [
    /<script/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /<iframe/gi,
    /<object/gi,
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(sanitized)) {
      return { valid: false, error: 'Description contains invalid characters' };
    }
  }

  return { valid: true, sanitized };
};

/**
 * Validate price input
 * - Must be a positive number
 * - Max value check (prevent overflow)
 */
export const validatePrice = (price) => {
  if (!price && price !== 0) {
    return { valid: false, error: 'Price is required' };
  }

  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  if (isNaN(numPrice)) {
    return { valid: false, error: 'Price must be a valid number' };
  }
  
  if (numPrice < 0) {
    return { valid: false, error: 'Price cannot be negative' };
  }
  
  if (numPrice > 999999999) {
    return { valid: false, error: 'Price is too large' };
  }

  return { valid: true, sanitized: numPrice };
};

/**
 * Validate search query
 * - Max 200 characters
 * - No MongoDB operators
 * - No dangerous patterns
 */
export const validateSearchQuery = (query) => {
  if (!query || typeof query !== 'string') {
    return { valid: true, sanitized: '' }; // Empty query is valid
  }

  const sanitized = sanitizeInput(query);
  
  if (sanitized.length > 200) {
    return { valid: false, error: 'Search query too long (max 200 characters)' };
  }

  // Block MongoDB operators
  const mongoOperators = [
    /\$ne/i, /\$gt/i, /\$lt/i, /\$gte/i, /\$lte/i, /\$in/i, /\$nin/i,
    /\$exists/i, /\$regex/i, /\$or/i, /\$and/i, /\$not/i, /\$nor/i,
    /\$where/i, /\$text/i, /\$search/i, /\$elemMatch/i
  ];

  for (const pattern of mongoOperators) {
    if (pattern.test(sanitized)) {
      return { valid: false, error: 'Invalid characters in search query' };
    }
  }

  // Block dangerous JavaScript patterns
  const dangerousPatterns = [
    /<script/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /<iframe/gi,
    /eval\(/gi,
    /expression\(/gi,
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(sanitized)) {
      return { valid: false, error: 'Invalid characters in search query' };
    }
  }

  return { valid: true, sanitized };
};

/**
 * Validate text field (generic)
 * - Optional length limits
 * - Sanitizes input
 */
export const validateTextField = (text, options = {}) => {
  const {
    minLength = 0,
    maxLength = 1000,
    required = false,
    fieldName = 'Field'
  } = options;

  if (!text && !required) {
    return { valid: true, sanitized: '' };
  }

  if (!text && required) {
    return { valid: false, error: `${fieldName} is required` };
  }

  if (typeof text !== 'string') {
    return { valid: false, error: `${fieldName} must be text` };
  }

  const sanitized = sanitizeInput(text);
  
  if (sanitized.length < minLength) {
    return { valid: false, error: `${fieldName} must be at least ${minLength} characters` };
  }
  
  if (sanitized.length > maxLength) {
    return { valid: false, error: `${fieldName} must be less than ${maxLength} characters` };
  }

  return { valid: true, sanitized };
};

/**
 * Validate numeric field
 * - Must be a number
 * - Optional min/max
 */
export const validateNumber = (value, options = {}) => {
  const {
    min = null,
    max = null,
    required = false,
    fieldName = 'Field'
  } = options;

  if (!value && value !== 0 && !required) {
    return { valid: true, sanitized: null };
  }

  if (!value && value !== 0 && required) {
    return { valid: false, error: `${fieldName} is required` };
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return { valid: false, error: `${fieldName} must be a valid number` };
  }
  
  if (min !== null && numValue < min) {
    return { valid: false, error: `${fieldName} must be at least ${min}` };
  }
  
  if (max !== null && numValue > max) {
    return { valid: false, error: `${fieldName} must be at most ${max}` };
  }

  return { valid: true, sanitized: numValue };
};

/**
 * Sanitize form data object
 * Recursively sanitizes all string values in an object
 */
export const sanitizeFormData = (formData) => {
  if (!formData || typeof formData !== 'object') {
    return formData;
  }

  const sanitized = {};
  
  for (const [key, value] of Object.entries(formData)) {
    if (typeof value === 'string') {
      // Don't sanitize certain fields that might need special characters
      if (key === 'description') {
        // Description can have more characters, but still sanitize dangerous patterns
        sanitized[key] = sanitizeInput(value);
      } else if (key === 'title' || key === 'breed' || key === 'type') {
        sanitized[key] = sanitizeInput(value);
      } else {
        sanitized[key] = sanitizeInput(value);
      }
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map(item => 
        typeof item === 'string' ? sanitizeInput(item) : item
      );
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};

/**
 * Validate file upload
 * - Check file type
 * - Check file size
 * - Check file name
 */
export const validateFile = (file, options = {}) => {
  const {
    maxSize = 10 * 1024 * 1024, // 10MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg', 'image/gif'],
    allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif']
  } = options;

  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  // Check file size
  if (file.size > maxSize) {
    return { valid: false, error: `File size exceeds ${maxSize / 1024 / 1024}MB limit` };
  }

  // Check MIME type
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'Invalid file type. Only images are allowed.' };
  }

  // Check file extension
  const fileName = file.name.toLowerCase();
  const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
  
  if (!hasValidExtension) {
    return { valid: false, error: 'Invalid file extension' };
  }

  // Sanitize filename
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  
  return { valid: true, sanitizedFileName };
};

/**
 * Prevent XSS in React by creating safe text nodes
 * Use this when rendering user input
 */
export const createSafeText = (text) => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  return escapeHtml(text);
};
